import { getPinsByTag, getUserPins } from "@/services";

import { useMemoizedFn, useUpdateEffect } from "ahooks";

import HBSelect from "@/components/HBSelect";
import { useInfiniteList } from "@/utils/hooks/useInfiniteList";
import { checkCanUploadPin } from "@/utils/open";
import constantFactory from "@gaoding/constant-factory";
import { last, omit } from "lodash";
import { useRouter } from "next/router";
import { useMemo, useRef, useState } from "react";
import HBIcon from "../../HBIcon";
import HBMessage from "../../HBMessage";
import PinList from "../../PinList";
import styles from "../index.module.less";
import Empty from "./Empty";
import Loading from "./Loading";
import TabPanel from "./TabPanel";
import TagSelectModal from "./TagSelectModal";

// 常量定义
const PAGE_SIZE = 30;
const EMPTY_IMAGE_URL =
  "https://cdn.dancf.com/fe-assets/img/e66686c56220ce789c966b6653936017.png";

/**
 * 构建获取 pins 的请求参数
 * @param {Object} options
 * @param {string} options.username 用户名
 * @param {string} options.type 筛选类型
 * @param {boolean} options.created 是否筛选我创建的
 * @param {number} options.page 页码
 * @param {Object} options.maxPin 用于分页的最后一条 pin
 * @returns {Object} 请求参数
 */
function buildQueryParams({ username, type, created, page, maxPin }) {
  const isPageBased = type || created;

  if (isPageBased) {
    // 基于页码的查询（有类型筛选条件）
    const params = {
      username,
      sort: "created_at", // 默认按创建时间降序排序
      page,
      material: type === "material",
    };

    // 非 material 类型时，添加 category 字段
    if (type && type !== "material") {
      params.category = type;
    }

    // 标记是否为我创建的
    if (created) {
      params.created = true;
    }

    return params;
  }

  // 基于 max 的查询（无筛选条件）
  return {
    username,
    max: maxPin?.pin_id,
    limit: PAGE_SIZE,
  };
}

export default function PinListPanel({
  user,
  isMe,
  initPins,
  showAction,
  hideCreateBtn = false,
  created = false,
}) {
  const router = useRouter();
  const { type, tag_name } = router.query;
  const [loading, setLoading] = useState(!!initPins);
  const maxRef = useRef(last(initPins));

  const queryFn = useMemoizedFn(async (page) => {
    if (page === 1) {
      // 第一页，重置 max
      maxRef.current = null;
    }

    const params = buildQueryParams({
      username: user.urlname,
      type,
      created,
      page,
      maxPin: maxRef.current,
    });

    setLoading(true);

    try {
      let res;

      if (tag_name) {
        res = await getPinsByTag({
          urlname: user.urlname,
          tag_name: encodeURIComponent(tag_name),
          limit: PAGE_SIZE,
          max: maxRef.current?.seq,
        });
      } else {
        res = await getUserPins(params);
      }

      maxRef.current = last(res?.pins);
      return res?.pins || [];
    } finally {
      setLoading(false);
    }
  });

  const {
    list: records,
    hasMore,
    onLoadMore,
    resetData,
  } = useInfiniteList(queryFn, {
    initialData: initPins,
  });

  // 排序类型列表
  const { SORT_LIST } = useMemo(() => {
    // 为了避免切换用户的情况
    return constantFactory("SORT", [
      {
        label: "筛选",
        type: "label",
      },
      {
        key: "label",
        label: "按标签筛选",
        value: "tag",
      },
    ]);
  }, []);

  // 我创建的 切换时，重置采集列表
  // 切换用户时，重置采集列表
  useUpdateEffect(() => {
    resetData();
  }, [user?.user_id, created, resetData, tag_name]);

  // 点击创建采集，显示图片上传弹窗
  const onCreateClick = useMemoizedFn(async () => {
    if (!checkCanUploadPin(user)) {
      return;
    }
    const { PinUploaderModal } = await import("@/components/PinCreator");

    PinUploaderModal.open({
      name: "pinUploaderModal",
      onOk: async () => {
        HBMessage.success("添加成功");
        resetData();
      },
    });
  });

  // 瀑布流第一个位置的，创建采集卡片
  const createBtn = (
    <div
      className={styles.createRect}
      key={"createBtn"}
      onClick={onCreateClick}
      data-gd-click="button_click"
      data-button-name="添加采集"
      title="上传采集"
    >
      <div className={styles.block}>
        <HBIcon type={"plus"} className={styles.icon} />
      </div>
    </div>
  );

  // 清除 tag_name 筛选
  const onClearTagFilter = useMemoizedFn(() => {
    router.push({
      query: omit(router.query, ["tag_name"]),
    });
  });

  // 打开标签选择弹窗
  const onFilterChange = useMemoizedFn(() => {
    TagSelectModal.open({
      urlname: user.urlname,
      onOk: (tag) => {
        router.push({
          query: {
            ...router.query,
            tag_name: tag[0],
          },
        });
      },
    });
  });

  // 显示 创建的 按钮
  const actions = useMemo(() => {
    if (!showAction) return null; // 不显示创建的按钮
    return (
      <div className={styles.actionsContent}>
        <div
          className={styles.filter}
          data-gd-click="button_click"
          data-button-name="标签"
        >
          {tag_name ? (
            <div className={styles.filterName}>
              {tag_name}
              <HBIcon
                type="ic_clear"
                className={styles.closeIcon}
                onClick={onClearTagFilter}
              />
            </div>
          ) : null}
          <HBSelect
            id="user_page_guide_6"
            allowClear={false}
            options={SORT_LIST}
            onChange={onFilterChange}
          />
        </div>
      </div>
    );
  }, [showAction, tag_name, SORT_LIST, onFilterChange, onClearTagFilter]);

  // 判断是否应该显示创建按钮
  const shouldShowCreateBtn = isMe && !hideCreateBtn;

  // 空状态内容
  const emptyContent = shouldShowCreateBtn ? (
    <>
      {createBtn}
      <div className={styles.empty}>还没有任何内容～</div>
    </>
  ) : (
    <Empty
      imageUrl={EMPTY_IMAGE_URL}
      title={`${isMe ? "您" : "Ta"}还没有上传任何内容～`}
    />
  );

  return (
    <TabPanel actions={actions}>
      {loading && !records.length ? (
        <Loading height={260} />
      ) : (
        <div style={{ marginTop: "16px" }}>
          {records.length ? (
            <PinList
              dataSource={records}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              hideUser
              hideCount={!isMe}
            >
              {shouldShowCreateBtn ? createBtn : null}
            </PinList>
          ) : (
            emptyContent
          )}
        </div>
      )}
    </TabPanel>
  );
}
